import { Redemption, Timestamp, RedemptionTraveler } from '@/api/backplane/BackplaneModels';
import {
  RedemptionDisplay,
  RedemptionExport,
  RedemptionTicketPrice,
  RedemptionTravelerDetails,
} from '@/models/RedemptionReportModels';
import { constants } from '@/utils/constants';
import { GetAgeBandByValue } from '@/utils/web-redemption';
import { FromSmallestUnit } from '@/utils/currency';

/**
 * returns the formatted total net price for the redemption
 * @param redemption
 */
export function getRedemptionTotalNetPrice(redemption: Redemption): string {
  const amount = redemption?.net?.amount ?? null;
  const currencyCode = redemption?.net?.currency_code ?? constants.DEFAULT_CURRENCY;
  if (amount === null) {
    return constants.EMPTY_STRING;
  }
  return FromSmallestUnit(amount, currencyCode);
}

/**
 * returns the formatted total retail price for the redemption
 * @param redemption
 */
export function getRedemptionTotalRetailPrice(redemption: Redemption): string {
  const amount = redemption?.retail?.amount ?? null;
  const currencyCode = redemption?.retail?.currency_code ?? constants.DEFAULT_CURRENCY;
  if (amount === null) {
    return constants.EMPTY_STRING;
  }
  return FromSmallestUnit(amount, currencyCode);
}

/**
 * returns the formatted traveler counts for the redemption
 * ex: 1 ADULT
 * @param travelers
 */
export function getTravelerCounts(travelers: RedemptionTraveler[]): string {
  const counts: { [key: string]: number } = {};

  for (const traveler of travelers) {
    const ageBand = GetAgeBandByValue(traveler.age_band);
    if (ageBand) {
      counts[ageBand] = (counts[ageBand] || 0) + 1;
    }
  }

  let result = '';
  for (const ageBand in counts) {
    result += `${counts[ageBand]}x ${ageBand}\n`;
  }

  return result.trim();
}

/**
 * transforms the traveler details for display
 * @param travelers
 */
export function transformRedemptionTravelers(travelers: any[]): RedemptionTravelerDetails[] {
  return travelers.map((t: any) => {
    return {
      full_name: t.full_name || constants.EMPTY_STRING,
      email: t.email || constants.EMPTY_STRING,
      mobile: t.mobile || constants.EMPTY_STRING,
      age_band: GetAgeBandByValue(t.age_band),
      is_lead: t.is_lead || false,
    };
  });
}

/**
 * transforms the redemption ticket prices for display
 * @param redemption
 */
export function transformRedemptionTicketPrices(redemption: Redemption): RedemptionTicketPrice[] {
  if (!redemption || !Array.isArray(redemption.items)) {
    return [];
  }

  const uniquePrices = new Map<string, RedemptionTicketPrice>();
  for (const item of redemption.items) {
    const ageBand = GetAgeBandByValue(item.traveler_type);
    const netAmount = item.net?.amount ?? null;
    const retailAmount = item.retail?.amount ?? null;
    const currencyCode = item.retail?.currency_code ?? constants.DEFAULT_CURRENCY;

    const keyObject = { ageBand, netAmount, retailAmount, currencyCode };
    const key = JSON.stringify(keyObject);

    if (!uniquePrices.has(key)) {
      const ticketPrice: RedemptionTicketPrice = {
        age_band: ageBand,
        currency_code: currencyCode,
        net_amount: netAmount ? FromSmallestUnit(netAmount, currencyCode) : constants.EMPTY_STRING,
        retail_amount: retailAmount ? FromSmallestUnit(retailAmount, currencyCode) : constants.EMPTY_STRING,
      };
      uniquePrices.set(key, ticketPrice);
    }
  }

  return Array.from(uniquePrices.values());
}

/**
 * transforms the travel details for export
 * @param travelers
 */
export function transformRedemptionTravelersForExport(travelers: RedemptionTravelerDetails[]): string {
  let details = constants.EMPTY_STRING;
  for (const traveler of travelers) {
    details += `Name: ${traveler.full_name}\n`;
    details += `Email: ${traveler.email}\n`;
    details += `Mobile: ${traveler.mobile}\n`;
    details += `Age Band: ${traveler.age_band}\n`;
    details += `Lead: ${traveler.is_lead}\n\n`;
  }
  return details.trim();
}

/**
 * transforms the ticket price details for export
 * @param prices
 */
export function transformRedemptionPricesForExport(prices: RedemptionTicketPrice[]): string {
  let details = constants.EMPTY_STRING;
  for (const price of prices) {
    details += `Age Band: ${price.age_band}\n`;
    details += `Net: ${price.net_amount}\n`;
    details += `Retail: ${price.retail_amount}\n\n`;
  }
  return details.trim();
}

/**
 * formats the timestamp into RFC3339 string
 * @param timestamp
 */
function formatTimestamp(timestamp: Timestamp): string {
  if (!timestamp) {
    return constants.EMPTY_STRING;
  }
  const milliseconds = timestamp.seconds * 1000;
  const date = new Date(milliseconds);
  return date.toISOString();
}

/**
 * transforms the redemption data for display in the UI
 * @param redemptions
 */
export function transformRedemptionsForDisplay(redemptions: Redemption[]): RedemptionDisplay[] {
  if (!redemptions || !Array.isArray(redemptions)) {
    return [];
  }
  return redemptions.map((r: Redemption): RedemptionDisplay => {
    return {
      supplier: r.supplier.name || constants.EMPTY_STRING,
      reseller: r.reseller.name || constants.EMPTY_STRING,
      ticket_ref: r.supplier_ref || r.scanned_reference || constants.EMPTY_STRING,
      net_amount: getRedemptionTotalNetPrice(r),
      retail_amount: getRedemptionTotalRetailPrice(r),
      time_redeemed: formatTimestamp(r.timestamp),
      operator: r.source?.operator_id || constants.EMPTY_STRING,
      traveler_count: getTravelerCounts(r.travelers),
    };
  });
}

/**
 * transforms the redemption data for download export
 * @param redemptions
 */
export function transformRedemptionsForExport(redemptions: Redemption[]): RedemptionExport[] {
  if (!redemptions || !Array.isArray(redemptions)) {
    return [];
  }
  return redemptions.map((r: Redemption): RedemptionExport => {
    return {
      id: r.id,
      supplier: r.supplier.name || constants.EMPTY_STRING,
      reseller: r.reseller.name || constants.EMPTY_STRING,
      supplier_ref: r.supplier_ref || constants.EMPTY_STRING,
      reseller_booking_ref: r.reseller_booking_ref || constants.EMPTY_STRING,
      reseller_ref: r.reseller_ref || constants.EMPTY_STRING,
      scanned_ref: r.scanned_reference || constants.EMPTY_STRING,
      operator: r.source?.operator_id || constants.EMPTY_STRING,
      time_redeemed: formatTimestamp(r.timestamp),
      net_amount: getRedemptionTotalNetPrice(r),
      retail_amount: getRedemptionTotalRetailPrice(r),
      per_ticket_amount: transformRedemptionPricesForExport(transformRedemptionTicketPrices(r)),
      traveler_count: getTravelerCounts(r.travelers),
      traveler_details: transformRedemptionTravelersForExport(transformRedemptionTravelers(r.travelers)),
    };
  });
}
